/**
 * Copying product data between the two shops, decided without touching Firestore.
 *
 * VCE and HVO sell the same physical product out of one freezer, but their products live in two
 * Firebase projects that share no key at all - no sku, no ean, no article number. The only way
 * across is a link someone made by hand (`productLinks`, see data/hooks/useProductLinks), which
 * is why half this file is about *suggesting* that link rather than computing it.
 *
 * Everything here is pure: no Firestore, no React. It decides what a sync would do; the hooks
 * (useProductLinks / useProductFields) do the reading and writing. Same split as pickRoute.js,
 * and for the same reason - this is the part that has to be right, so it has to be testable.
 */

import {
  getSyncableProperty,
  isSyncableProperty,
  syncPropertyLabel,
  WAREHOUSE_FIELDS,
} from "../constants/ProductSyncConstant";
import { normalizeProductName } from "./pickRoute";

export const SHOP_VCE = "VCE";
export const SHOP_HVO = "HVO";

/** The other shop. There are only two, and getting this backwards writes to the wrong project. */
export const otherShop = (shopSource) => (shopSource === SHOP_HVO ? SHOP_VCE : SHOP_HVO);

/**
 * A gram value as a number, or null when there isn't one.
 *
 * Firestore holds these as a number in some documents and as a numeric string in others (which
 * is why api types them as `Object`), because the old CMS form wrote whatever the input gave it.
 * Everything downstream - comparing two shops, box-size prediction - needs one type, so this is
 * the only place that decides what a gram value is.
 *
 * Empty means "not filled in" and gives null. Anything that is not a plain number - "abc",
 * "12kg", NaN, a negative weight - is nonsense rather than an empty value, and also gives null;
 * callers that need to tell the two apart use `parseGramsInput`.
 */
export const normaliseGrams = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return null;

  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  const text = value.toString().trim().replace(",", ".");
  if (text === "" || !/^\d+(\.\d+)?$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Same parse, but tells an empty field ("clear this value") apart from a typo ("abc"), so the
 * screen can refuse the typo instead of silently wiping a weight the warehouse depends on.
 */
export const parseGramsInput = (value) => {
  if (value === null || value === undefined || value === "" || (typeof value === "string" && !value.trim())) {
    return { ok: true, value: null, empty: true };
  }
  const parsed = normaliseGrams(value);
  if (parsed === null) return { ok: false, value: null, empty: false };
  return { ok: true, value: parsed, empty: false };
};

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

/**
 * Deep equality, good enough for product field values: primitives, arrays (ingredients,
 * traceability) and flat-ish maps (nutritionalValue). Key order must not count as a difference,
 * or a re-saved map would show up as a change forever.
 */
const deepEqual = (a, b) => {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) {
    // null and undefined both mean "not set" in Firestore documents written by two CMSes
    return (a === null || a === undefined) && (b === null || b === undefined);
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && deepEqual(a[key], b[key]));
  }
  return false;
};

/**
 * Compare two values of a given property. For gram fields this normalises first, so the number
 * 2400 and the string "2400" - both of which occur in Firestore - are the same value and do not
 * produce a pointless "wil je dit ook toepassen?" prompt.
 */
export const valuesEqualForProperty = (key, a, b) => {
  const property = getSyncableProperty(key);
  if (!!property && property.type === "grams") return normaliseGrams(a) === normaliseGrams(b);
  return deepEqual(a, b);
};

/** The value as it should be written for this property. */
const valueToWrite = (key, value) => {
  const property = getSyncableProperty(key);
  if (!!property && property.type === "grams") return normaliseGrams(value);
  return value === undefined ? null : value;
};

/**
 * Work out what copying `changedProperties` from `source` to `target` would do, without doing it.
 *
 * Same shape of answer as buildRouteSync in pickRoute.js, and for the same reason: the page has
 * to be able to show "je hebt voor de kipnuggets van het Vleescentrum de inhoud op 3 kg gezet,
 * ik zie hetzelfde product bij Halalvlees Online staan - ook daar toepassen?" *before* anything
 * is written. Nothing is applied here.
 *
 * Refusals are reported rather than dropped silently, so the screen can explain why a property
 * the user just changed is not in the prompt:
 *  - `not-syncable` - outside the registry, or on NEVER_SYNC (copying it would break the other
 *    shop). Cannot be overridden by passing it in `syncedProperties`.
 *  - `not-selected` - syncable, but this link is not configured to sync it (price, by default).
 *
 * @param {Object} source - the product just edited (plain field values)
 * @param {Object} target - the linked product in the other shop
 * @param {string[]} changedProperties - keys the user changed
 * @param {string[]} syncedProperties - this link's opted-in keys
 */
export const buildSyncPlan = ({ source, target, changedProperties, syncedProperties }) => {
  const sourceValues = source || {};
  const targetValues = target || {};
  const selected = new Set((syncedProperties || []).filter(isSyncableProperty));

  const properties = [];
  const skipped = [];

  const seen = new Set();
  (changedProperties || []).forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);

    // The registry gate first: an explicitly passed NEVER_SYNC key must never get through,
    // whatever `syncedProperties` says.
    if (!isSyncableProperty(key)) {
      skipped.push({ key, label: syncPropertyLabel(key), reason: "not-syncable" });
      return;
    }
    if (!selected.has(key)) {
      skipped.push({ key, label: syncPropertyLabel(key), reason: "not-selected" });
      return;
    }

    const from = targetValues[key];
    const to = sourceValues[key];
    properties.push({
      key,
      label: syncPropertyLabel(key),
      from: from === undefined ? null : from,
      to: valueToWrite(key, to),
      // Already the same on both sides: kept in the plan so the screen can show it as
      // "staat er al goed", but it must not make the prompt appear for nothing.
      willChange: !valuesEqualForProperty(key, from, to),
    });
  });

  const changes = properties.filter((property) => property.willChange);

  return {
    properties,
    changes,
    skipped,
    hasChanges: changes.length > 0,
    /** The targeted patch to hand to Firestore - only the properties that actually change. */
    values: changes.reduce((patch, property) => ({ ...patch, [property.key]: property.to }), {}),
  };
};

/** The side of a link that is not `shopSource`. */
export const linkCounterpart = (link, shopSource) => {
  if (!link) return null;
  return shopSource === SHOP_VCE
    ? { shopSource: SHOP_HVO, productId: link.hvoProductId, name: link.hvoName || "" }
    : { shopSource: SHOP_VCE, productId: link.vceProductId, name: link.vceName || "" };
};

/** The link a product belongs to, looked up from either side. */
export const findLinkForProduct = (links, shopSource, productId) => {
  if (!productId) return null;
  return (
    (links || []).find((link) =>
      shopSource === SHOP_VCE ? link.vceProductId === productId : link.hvoProductId === productId,
    ) || null
  );
};

/**
 * The document id a link between these two products must be written under.
 *
 * Derived from the VCE product id rather than left to Firestore's auto-id, and that is the
 * whole point: the one-to-one rule is checked entirely in the browser, against a list of links
 * that was read a moment ago. Two people who open the proposal at the same time each read "no
 * links yet", each tick the same 250 rows, and each write 250 fresh auto-ids - two full sets,
 * colliding with nothing, and `findLinkForProduct` then returns whichever of the two duplicates
 * an unordered read hands over first. For a single hand-made link that was one wrong row; for
 * the bulk round it is the entire catalogue, twice, in one click.
 *
 * A deterministic id turns that race into an overwrite: both rounds address the same document,
 * the last writer wins, and one link comes out instead of two. It does not make the write
 * transactional - the second round can still overwrite the first with a different counterpart -
 * but "one link per VCE product" is the invariant everything downstream depends on, and this
 * makes it hold in the database rather than only in whichever tab wrote last.
 *
 * VCE is the key side because `productLinks` lives in the VCE project and proposals run from
 * VCE outwards; the HVO side stays guarded by the in-batch and already-linked checks.
 *
 * Returns null when the product id would not make a usable document id - Firestore refuses an
 * empty one, `.` and `..`, anything containing a slash, and the reserved `__…__` form. Those
 * cannot occur for a Firestore auto-id, which is what both shops use, but a product id is data
 * and this must not turn into a write to the wrong path. The caller falls back to an auto-id,
 * which is no worse than what it did before.
 */
export const linkDocumentId = (vceProductId) => {
  if (!vceProductId || typeof vceProductId !== "string") return null;
  if (vceProductId.includes("/")) return null;
  if (vceProductId === "." || vceProductId === "..") return null;
  if (/^__.*__$/.test(vceProductId)) return null;
  // Firestore's limit is 1500 bytes, not characters; a product id is ASCII in practice but the
  // byte length is what the SDK checks. encodeURIComponent writes one %XX per UTF-8 byte and
  // leaves ASCII alone, so this counts bytes without needing TextEncoder (absent in jsdom).
  // encodeURIComponent gooit URIError op een losse surrogate. Het contract van deze functie is
  // "null zodra het id onbruikbaar is", en een worp hier zou de hele bulkronde afbreken vóór de
  // eerste batch in plaats van deze ene rij op een auto-id te laten terugvallen.
  try {
    if (encodeURIComponent(vceProductId).replace(/%[0-9A-F]{2}/gi, "b").length > 1500) return null;
  } catch (error) {
    return null;
  }
  return vceProductId;
};

/**
 * The product ids of one shop that are already spoken for by a link.
 *
 * Exported because the bulk proposal needs exactly the same answer as the per-product
 * suggestion does: a second list built the other way round would drift from this one, and the
 * thing it decides is whether a one-to-one link gets made twice.
 */
export const linkedProductIdsForShop = (links, shopSource) =>
  new Set(
    (links || [])
      .map((link) => (shopSource === SHOP_VCE ? link.vceProductId : link.hvoProductId))
      .filter((productId) => !!productId),
  );

const linkedProductIds = linkedProductIdsForShop;

/** Product ids of the other shop sitting on the same pick route entry as this product. */
const pickRouteCounterpartIds = (pickRoute, product) => {
  const target = otherShop(product.shopSource);
  const entry = (pickRoute || []).find((candidate) =>
    ((candidate && candidate.products) || []).some(
      (entryProduct) =>
        entryProduct.shopSource === product.shopSource && entryProduct.productId === product.id,
    ),
  );
  if (!entry) return new Set();
  return new Set(
    (entry.products || [])
      .filter((entryProduct) => entryProduct.shopSource === target && !!entryProduct.productId)
      .map((entryProduct) => entryProduct.productId),
  );
};

/**
 * The normalised name with every space and separator dropped: "kipnuggets1kg".
 *
 * The two shops spell the same product differently - "Kipnuggets 1 kg" here, "Kip nuggets 1kg"
 * there - and that is the normal case during the catch-up, not an exception. Normalising to
 * words is not enough for that (the two differ in where the spaces are), and a substring match
 * does not catch it either (neither name contains the other). Comparing the letters and digits
 * alone does, and it stays a statement about the same name rather than a guess: it only pulls
 * spacing and punctuation out of the comparison.
 *
 * Deliberately here and not in `normalizeProductName`: the pick route matches lines to freezer
 * spots by name, where dropping the spaces would let "kip 1kg" and "kip1 kg" collide.
 */
const compactOfNormalized = (normalized) => normalized.replace(/[^a-z0-9]+/g, "");

export const compactProductName = (name) => compactOfNormalized(normalizeProductName(name));

/**
 * The two normalised forms of a product's name, computed once per product document.
 *
 * `suggestLinkCandidates` answers one source product, and the bulk proposal calls it once per
 * source product over the *same* array of candidates. Normalising inside that loop therefore
 * normalises every candidate name once per source product: two catalogues of a thousand
 * products is a million regex passes to produce a thousand distinct answers. That is the
 * whole cost of the screen, and it is paid during render.
 *
 * A WeakMap on the product object collapses it to one pass per product. Keyed on the object,
 * so nothing has to be threaded through the call signature and nothing is retained after the
 * products are dropped; the stored `name` is compared as well, so a product whose name was
 * edited in place still gets a fresh answer rather than a stale one.
 *
 * This is a cache, not state: it cannot change what any call returns, only how long it takes.
 */
const nameFormsCache = new WeakMap();

const nameFormsOf = (product) => {
  const name = (product && product.name) || "";

  if (!product || typeof product !== "object") {
    const normalized = normalizeProductName(name);
    return { name, normalized, compact: compactOfNormalized(normalized) };
  }

  const cached = nameFormsCache.get(product);
  if (!!cached && cached.name === name) return cached;

  const normalized = normalizeProductName(name);
  const forms = { name, normalized, compact: compactOfNormalized(normalized) };
  nameFormsCache.set(product, forms);
  return forms;
};

/** Shortest sensible partial match: one normalised name contains the other. */
const isPartialNameMatch = (a, b) => {
  if (!a || !b || a === b) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  // Below four characters "kip" matches half the freezer, which is worse than no suggestion.
  if (shorter.length < 4) return false;
  return longer.includes(shorter);
};

/**
 * Whether two product names are recognisably about the same product - the same check the name
 * levels below use, asked as a yes/no rather than as a ranking.
 */
const namesLookRelated = (a, b) => {
  const left = normalizeProductName(a);
  const right = normalizeProductName(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (compactProductName(a) === compactProductName(b)) return true;
  return isPartialNameMatch(left, right);
};

/**
 * Two names that have nothing to do with each other - both filled in, and not related by any
 * of the name levels.
 *
 * A product with no name at all is *unknown*, not divergent: flagging "de namen verschillen"
 * on a product that simply has no name would send someone looking for a problem that is not
 * there. So a missing name is never a mismatch.
 */
export const namesDiverge = (a, b) => {
  const left = normalizeProductName(a);
  const right = normalizeProductName(b);
  if (!left || !right) return false;
  return !namesLookRelated(a, b);
};

const REASONS = {
  // The document id itself. HVO's product collection was copied from VCE's (owner, 2026-09-25),
  // so the same product can carry the same auto-id in both projects. When it does, this is not
  // evidence about a name - it is the same document, and it beats everything below.
  //
  // It is still a hypothesis: the collection may also have been recreated with fresh ids, in
  // which case this level simply never fires and the name levels do the work as before. Which
  // of the two it is shows up in the proposal summary, not in an assumption made here.
  id: { score: 5, label: "Zelfde product-id" },
  pickRoute: { score: 4, label: "Staat samen op de pickroute" },
  name: { score: 3, label: "Zelfde naam" },
  compact: { score: 2, label: "Zelfde naam, andere schrijfwijze" },
  partial: { score: 1, label: "Naam lijkt erop" },
};

/** The ranking levels, strongest first - exported so the overview can label and group them. */
export const LINK_REASONS = REASONS;

export const linkReasonLabel = (reason) => (REASONS[reason] || {}).label || reason;

/**
 * Suggest which product in the other shop is the same product as this one.
 *
 * Ordered by how much the evidence is worth. The document id is the strongest: HVO's product
 * collection is a copy of VCE's, so the same product can literally be the same document id in
 * both projects, and that is a fact rather than a resemblance. Then a pick route entry holding
 * both products - someone already put them on the same spot in the freezer by hand, which is
 * the statement "this is one product". Then an exact match on the normalised name, then the
 * same name written with different spacing ("Kipnuggets 1 kg" / "Kip nuggets 1kg"), then a
 * partial one. Nothing here links anything - a human confirms, because a wrong link would copy
 * one product's pick weight onto a different piece of meat.
 *
 * `nameMismatch` says the two names have nothing in common. On a name-based level that can
 * never happen; on an id match it is the one thing that argues against the copy hypothesis, so
 * it travels with the suggestion instead of quietly removing it: if the ids were reshuffled
 * rather than copied, this is how it becomes visible.
 *
 * Products already linked are left out: a link is one-to-one, and offering a taken product
 * would only produce a link that has to be broken again.
 */
export const suggestLinkCandidates = ({ product, otherShopProducts, existingLinks, pickRoute }) => {
  if (!product || !product.id || !product.shopSource) return [];

  const targetShop = otherShop(product.shopSource);
  const taken = linkedProductIds(existingLinks, targetShop);
  const routeIds = pickRouteCounterpartIds(pickRoute, product);
  const sourceForms = nameFormsOf(product);
  const normalizedName = sourceForms.normalized;
  const compactName = sourceForms.compact;

  const suggestions = [];

  (otherShopProducts || []).forEach((candidate) => {
    if (!candidate || !candidate.id) return;
    // Guard against being handed the full both-shops product list
    if (!!candidate.shopSource && candidate.shopSource !== targetShop) return;
    if (taken.has(candidate.id)) return;

    // Identity first, names only if it fails. The two id checks are a Set lookup and a string
    // compare; normalising a name is a regex pass plus allocations, and the case this whole
    // feature is built for - HVO's collection being a copy of VCE's, so every product matches
    // on `id` - is exactly the case where all of that name work would be thrown away. With two
    // catalogues of a thousand products the old order paid for a million normalisations before
    // the first comparison that could have been decided by the id alone. This runs during
    // render, so that difference is dropped frames, not just CPU.
    let reason = null;
    let candidateName = null;

    if (candidate.id === product.id) {
      reason = "id";
    } else if (routeIds.has(candidate.id)) {
      reason = "pickRoute";
    } else {
      // One pass per product document for the whole round, not one per (source, candidate)
      // pair - see `nameFormsOf`.
      const candidateForms = nameFormsOf(candidate);
      candidateName = candidateForms.normalized;
      if (!!normalizedName && candidateName === normalizedName) reason = "name";
      else if (!!compactName && candidateForms.compact === compactName) reason = "compact";
      else if (isPartialNameMatch(normalizedName, candidateName)) reason = "partial";
    }

    if (!reason) return;

    // Only the id and pickRoute levels can contradict the names; the three name levels *are*
    // the claim that the names match, so asking again would normalise both names once more to
    // be told "no" every time.
    const nameMismatch =
      (reason === "id" || reason === "pickRoute") && namesDiverge(product.name, candidate.name);

    suggestions.push({
      product: candidate,
      productId: candidate.id,
      shopSource: targetShop,
      name: candidate.name || "",
      reason,
      reasonLabel: REASONS[reason].label,
      score: REASONS[reason].score,
      nameMismatch,
    });
  });

  return suggestions.sort((a, b) =>
    a.score !== b.score ? b.score - a.score : (a.name || "").localeCompare(b.name || "", "nl"),
  );
};

/**
 * Which of `fields` changed between loading the form and now.
 *
 * The old `cms/` is still running next to this one and writes the whole product document. If
 * someone saved there while this form was open, a blind write here would put the stale value
 * back without anyone noticing. So before writing we re-read the document and compare it to
 * what the form was loaded with; anything listed here means "someone else got there first" and
 * the screen has to say so rather than overwrite it.
 */
export const detectStaleWrite = ({ loadedValues, currentValues, fields }) => {
  // No snapshot of what was loaded means there is nothing to compare against - the caller
  // (a fresh product, a forced overwrite) has decided it does not need the check.
  if (!loadedValues) return [];

  const current = currentValues || {};
  const keys = fields && fields.length > 0 ? fields : WAREHOUSE_FIELDS;

  return keys
    .filter((key) => !valuesEqualForProperty(key, loadedValues[key], current[key]))
    .map((key) => ({
      key,
      label: syncPropertyLabel(key),
      loaded: loadedValues[key] === undefined ? null : loadedValues[key],
      current: current[key] === undefined ? null : current[key],
    }));
};

/**
 * Turn form input for the warehouse fields into the exact patch to write.
 *
 * Only the three fields, only the ones actually supplied, each normalised - never the whole
 * document, because `cms/` owns the rest of it. A gram field that is filled in with something
 * that is not a number comes back as `invalid` instead of being written as null: clearing a
 * pick weight by typo would send the picker looking for a box size that does not exist.
 */
export const buildWarehousePatch = (values) => {
  const patch = {};
  const invalid = [];

  WAREHOUSE_FIELDS.forEach((key) => {
    if (!values || !Object.prototype.hasOwnProperty.call(values, key)) return;

    const property = getSyncableProperty(key);
    if (!!property && property.type === "grams") {
      const parsed = parseGramsInput(values[key]);
      if (!parsed.ok) {
        invalid.push({ key, label: syncPropertyLabel(key), value: values[key] });
        return;
      }
      patch[key] = parsed.value;
      return;
    }

    const text = values[key];
    patch[key] = text === undefined || text === null || text === "" ? null : text.toString().trim();
  });

  return { patch, invalid };
};
